<script>
export default {
  emits: ['openInvoiceUpload'],
  props: {
    data1: Array,
    columns: Array,
    columnsDisplayed: Object,
    filterKey: String,
    bankFilter: String,
    perPage: Number,
    fetchDone: Boolean,
    showImportButton: {
      type: Boolean,
      default: false
    },
    groupBySupplier: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sortKey: '',
      sortOrders: this.columns.reduce((o, key) => ((o[key] = 1), o), {}),
      page: 1,

      pages: [],
      data: []
    }
  },

  computed: {
    displayedData() {
      if (this.groupBySupplier) {
        return this.groupedData();
      }
      return this.paginate(this.filteredData(this.data1));
    },
    displayedDataAll() {
      return this.filteredData(this.data1);
    }
  },

  watch: {
    displayedData() {
      this.setPages();
    }
  },
  methods: {
    statusClasses(color) {
      return {
        gray: 'bg-gray-100 text-gray-800',
        green: 'bg-green-100 text-green-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        blue: 'bg-blue-100 text-blue-800',
        purple: 'bg-purple-100 text-purple-800',
      }[color] || 'bg-gray-100 text-gray-800';
    },
    onImageLoadFailure(event) {
      event.target.src = 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png'
    },
    invoicesTotal(invoices) {
      return invoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0).toFixed(2)
    },
    sortBy(key) {
      this.sortKey = key
      this.sortOrders[key] = this.sortOrders[key] * -1
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    setPages() {
      this.pages = [];
      let numberOfPages = Math.ceil(this.filteredData(this.data1).length / this.perPage);
      for (let index = 1; index <= numberOfPages; index++) {
        this.pages.push(index);
      }
    },
    filteredData() {
      const sortKey = this.sortKey
      const filterKey = this.filterKey && this.filterKey.toLowerCase()
      const bankFilter = this.bankFilter
      const order = this.sortOrders[sortKey] || 1
      let data = this.data1

      // Filtre par banque
      if (bankFilter) {
        data = data.filter((row) => {
          return row.bank === bankFilter
        })
      }

      // Filtre par recherche texte
      if (filterKey) {
        data = data.filter((row) => {
          return Object.keys(row).some((key) => {
            return String(row[key]).toLowerCase().indexOf(filterKey) > -1
          })
        })
      }

      // Tri
      if (sortKey) {
        data = data.slice().sort((a, b) => {
          a = a[sortKey]
          b = b[sortKey]
          return (a === b ? 0 : a > b ? 1 : -1) * order
        })
      }
      return data
    },
    paginate(data) {
      let page = this.page;
      let perPage = this.perPage;
      let from = (page * perPage) - perPage;
      let to = (page * perPage);
      return data.slice(from, to);
    },
    groupedData() {
      const filtered = this.filteredData(this.data1);
      const grouped = {};

      // Group transactions by supplier
      filtered.forEach(transaction => {
        const supplierKey = transaction.company_name || transaction.from_name || 'Non défini';
        if (!grouped[supplierKey]) {
          grouped[supplierKey] = {
            supplierName: supplierKey,
            avatar: transaction.company_avatar || 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png',
            transactions: [],
            totalAmount: 0
          };
        }
        // Update avatar if current transaction has one and group doesn't
        if (transaction.company_avatar && !grouped[supplierKey].avatar) {
          grouped[supplierKey].avatar = transaction.company_avatar;
        }
        grouped[supplierKey].transactions.push(transaction);
        grouped[supplierKey].totalAmount += parseFloat(transaction.amount) || 0;
      });

      // Convert to array and sort by total amount descending
      return Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount);
    }
  }
}
</script>

<script setup>
import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/vue/solid'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/solid'

</script>

<template>

  <!-- Grouped view -->
  <div v-if="groupBySupplier && displayedData.length">
    <div v-for="group in displayedData" :key="group.supplierName" class="mb-6">
      <div class="bg-gray-100 px-3 py-2 border-b border-gray-300">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <img :src="group.avatar" @error="onImageLoadFailure($event)" class="h-7 w-7 rounded-full mr-2.5" alt=""/>
            <div class="flex items-baseline gap-2">
              <h3 class="text-sm font-semibold text-gray-900">{{ group.supplierName }}</h3>
              <p class="text-xs text-gray-500">{{ group.transactions.length }} transaction(s) - Total: {{ group.totalAmount.toFixed(2) }} EUR</p>
            </div>
          </div>
        </div>
      </div>
      <table class="min-w-full divide-y divide-gray-300">
        <thead class="bg-gray-50">
        <tr>
          <th class="w-1/4 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Description</th>
          <th class="w-1/6 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Statut</th>
          <th class="w-1/6 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Date</th>
          <th class="w-1/6 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Montant</th>
          <th class="w-1/6 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Banque</th>
          <th class="w-1/6 px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"></th>
        </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
        <tr v-for="transaction in group.transactions" :key="transaction.id"
            class="cursor-pointer hover:bg-gray-50"
            @click="$router.push('/transaction/' + transaction.button)">
          <td class="w-1/4 overflow-hidden whitespace-wrap px-2 py-1 text-sm text-gray-500">
            <div class="max-w-[240px] truncate font-medium text-gray-900" :title="transaction.description">{{ transaction.description }}</div>
          </td>
          <td class="w-1/6 overflow-hidden whitespace-wrap px-2 py-1 text-sm text-gray-500">
            <span class="inline-flex whitespace-nowrap rounded-full px-2 text-xs font-semibold leading-5"
                  :class="statusClasses(transaction.status_color)">{{ transaction.status_name }}</span>
          </td>
          <td class="w-1/6 overflow-hidden whitespace-nowrap px-2 py-1 text-sm text-gray-500">{{ transaction.date }}</td>
          <td class="w-1/6 overflow-hidden whitespace-nowrap px-2 py-1 text-sm text-gray-500">
            <span class="text-gray-900">{{ transaction.amount }} EUR</span>
            <span class="ml-1 text-xs text-gray-500" v-if="transaction.local_amount !== transaction.amount">({{ transaction.local_amount }} {{ transaction.local_currency }})</span>
          </td>
          <td class="w-1/6 overflow-hidden whitespace-wrap px-2 py-1 text-sm text-gray-500">{{ transaction.bank }}</td>
          <td class="whitespace-nowrap px-2 py-1 text-sm text-gray-500" @click.stop>
            <div class="flex space-x-1.5">
              <button v-if="showImportButton" type="button"
                      @click="$emit('openInvoiceUpload', transaction.company_id)"
                      class="inline-flex items-center whitespace-nowrap rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 shadow-sm hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Importer facture
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Standard list view -->
  <div v-else-if="displayedData.length">
    <table
        class="min-w-full divide-y divide-gray-300"
    >
      <thead class="bg-gray-50">
      <tr>
        <th
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            :class="{ active: sortKey == index }"
            class="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
            @click="sortBy(index)"
        >
          <a
              href="#"
              class="group inline-flex"
          >
            {{ capitalize(item) }}
            <span class="ml-1.5 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300">
              <ChevronDownIcon
                  v-if="sortOrders[index] > 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />
              <ChevronUpIcon
                  v-if="sortOrders[index] < 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />

            </span>
          </a>
          <span
              class="arrow"
              :class="sortOrders[index] > 0 ? 'asc' : 'dsc'"
          />
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white">
      <tr
          v-for="entry in displayedData"
          :key="entry"
          class="cursor-pointer hover:bg-gray-50"
          @click="$router.push('/transaction/' + entry['button'])"
      >
        <td
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            class="overflow-hidden  whitespace-wrap px-2 py-1 text-sm text-gray-500"
        >
          <div class="flex items-center" v-if="index === 'from_name'">
            <div class="h-6 w-6 flex-shrink-0">
              <img @error="onImageLoadFailure($event)"
                   class="h-6 w-6 rounded-full"
                   :src="entry['company_avatar']"
                   alt=""/>
            </div>
            <div class="ml-2.5 min-w-0">
              <div class="truncate font-medium text-gray-900" v-if="entry['company_name'] != null">{{ entry['company_name'] }}</div>
              <div class="truncate font-medium text-gray-900" v-if="entry['company_name'] == null">{{ entry['from_name'] }}</div>

              <div v-if="entry['invoices'] && entry['invoices'].length"
                   class="flex items-center text-xs text-emerald-700">
                <template v-for="inv in entry['invoices']" :key="inv.id">
                  <img v-if="inv.company_avatar" :src="inv.company_avatar"
                       @error="onImageLoadFailure($event)"
                       class="h-3.5 w-3.5 rounded-full mr-1" :title="inv.company_name" alt=""/>
                </template>
                <span>{{ entry['invoices'].length }} facture(s) associée(s) — {{ invoicesTotal(entry['invoices']) }} EUR</span>
              </div>
            </div>
          </div>

          <span v-else-if="index === 'status_name'"
                class="inline-flex whitespace-nowrap rounded-full px-2 text-xs font-semibold leading-5"
                :class="statusClasses(entry['status_color'])">{{ entry['status_name'] }}</span>

          <div v-else-if="index === 'amount'" class="whitespace-nowrap">
            <span class="text-gray-900">{{ entry['amount'] }} EUR</span>
            <span class="ml-1 text-xs text-gray-500" v-if="entry['local_amount'] !== entry['amount']">({{ entry['local_amount'] }}
              {{ entry['local_currency'] }})</span>
          </div>

          <div v-else-if="index === 'date' || index === 'created_at'" class="min-w-[92px] whitespace-nowrap">
            {{ entry[index] }}
          </div>

          <div v-else-if="index === 'description'" class="max-w-[200px] truncate" :title="entry['description']">
            {{ entry['description'] }}
          </div>

          <div v-else-if="index === 'button'" class="whitespace-nowrap" @click.stop>
            <div class="flex space-x-1.5">
              <button v-if="showImportButton" type="button"
                      @click="$emit('openInvoiceUpload', entry['company_id'])"
                      class="inline-flex items-center whitespace-nowrap rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 shadow-sm hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Importer facture
              </button>
            </div>
          </div>

          <div v-else>
            {{ entry[index] }}
          </div>

        </td>
      </tr>
      </tbody>
    </table>

    <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div class="flex-1 flex justify-between sm:hidden">
        <a href="#" v-if="page != 1" @click="page--"
           class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Previous </a>
        <a href="#" @click="page++" v-if="page < pages.length"
           class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Next </a>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            {{ ' ' }}
            <span class="font-medium">{{ page }}</span>
            {{ ' ' }}
            to
            {{ ' ' }}
            <span class="font-medium">{{ pages.length }}</span>
            {{ ' ' }}
            of
            {{ ' ' }}
            <span class="font-medium">{{ displayedDataAll.length }}</span>
            {{ ' ' }}
            results
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <a href="#" v-if="page != 1" @click="page--"
               class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Previous</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
            <!-- Current: "z-10 bg-indigo-50 border-indigo-500 text-indigo-600", Default: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50" -->
            <a href="#" aria-current="page" v-bind:key="pageNumber" type="button"
               v-for="pageNumber in pages.slice(page-1, page+5)" @click="page = pageNumber"
               :class="[ pageNumber === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium': 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium']">
              {{ pageNumber }}</a>

            <a href="#" @click="page++" v-if="page < pages.length"
               class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Next</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <div
      v-else-if="fetchDone === false"
      class="bg-white"
  >
    <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8 ">
      <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        <div
            class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status">
  <span
      class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]"
  >Loading...</span
  >
        </div>
        <span class="block"></span>
      </h2>

    </div>
  </div>
  <div
      v-else-if="displayedData.length === 0"
      class="bg-white"
  >
    <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
      <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        <span v-if="filterKey" class="block">Aucun résultat pour « {{ filterKey }} »</span>
        <span v-else class="block">Il n'y a aucune transaction ici !</span>
        <span class="block"></span>
      </h2>

    </div>
  </div>

</template>
